import React, { useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import { Stack } from "@mui/material";

export const parseExtension = name =>
  name.match(/\.[^.]*$|$/i)[0].toLowerCase();

export const acceptRegex = accept =>
  new RegExp(
    "^(" + accept.replace(/[, ]+/g, "|").replace(/\/\*/g, "/.*") + ")$",
    "i",
  );

export const isValidType = (accept, type) =>
  !accept || acceptRegex(accept).test(type);

export const isValidExtension = (accept, extension) =>
  !accept || acceptRegex(accept).test(extension);

export const isValidAccept = (accept, file) =>
  !accept ||
  isValidType(accept, file.type) ||
  isValidExtension(accept, parseExtension(file.name));

export const isValidMaxSize = (maxSize, size) => {
  if (!maxSize && typeof maxSize !== "number") {
    return true;
  }

  const maxSizeNum = parseInt(maxSize, 10);
  return isNaN(maxSizeNum) ? false : size <= maxSizeNum;
};

const stopEvent = e => {
  e.stopPropagation();
  e.preventDefault();
};

const initialState = {
  draggedOver: false,
};

const useStyles = makeStyles(theme => ({
  container: {
    position: "relative",
  },

  button: {
    height: "inherit",
    width: "100%",
  },

  buttonLabel: {
    display: "block",
    textAlign: "center",
  },

  dropZone: {
    position: "relative",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: theme.palette.primary.main,
    borderRadius: 3,
    overflow: "hidden",
    padding: 14,
    display: "flex",
    flexDirection: "column",
  },

  dropZoneDraggedOver: {
    position: "relative",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.primary.dark,
    borderRadius: 3,
    overflow: "hidden",
    padding: 14,
    display: "flex",
    flexDirection: "column",
  },

  dropZoneError: {
    borderColor: theme.palette.error.main,
  },

  selectFileRow: {
    display: "flex",
    justifyContent: "center",
  },

  fileNameRow: {
    display: "flex",
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: theme.palette.primary.main,
    marginTop: 20,
  },

  errorRow: {
    display: "flex",
    margin: "10px 0",
  },

  errorLabel: {
    color: theme.palette.error.main,
    fontSize: "1.6rem",
  },

  uploadIcon: {
    color: theme.palette.primary.main,
    fontSize: "3rem",
  },

  dropHere: {
    color: theme.palette.primary.main,
    marginLeft: 8,
    fontSize: "1.6rem",
  },

  fileInput: {
    display: "none",
  },

  fileName: {
    flex: 1,
    fontSize: "1.6rem",
    color: theme.palette.primary.main,
  },

  closeButton: {
    cursor: "pointer",
  },
}));

const FileUploadInput = props => {
  const [refCount, setRefCount] = useState(0);
  const [fileInput, setFileInput] = useState(null);
  const [state, setState] = useState(initialState);
  const classes = useStyles();

  useEffect(() => {
    if (refCount === 1) {
      setState({
        draggedOver: true,
      });
    } else if (refCount === 0) {
      setState(initialState);
    }
  }, [refCount]);

  const fileChanged = file => {
    if (!isValidAccept(props.accept, file)) {
      return;
    }

    if (!isValidMaxSize(props.maxSize, file.size)) {
      return;
    }

    if (props.onChange) {
      props.onChange(file);
    }
  };

  const fileRemoved = () => {
    if (props.onChange) {
      props.onChange(null);
    }

    setFileInput(null);
  };

  const dragHandler = handler => {
    return e => {
      stopEvent(e);

      if (!props.value && handler) {
        handler(e);
      }
    };
  };

  const getDropZoneClassName = () => {
    return state.draggedOver ? classes.dropZoneDraggedOver : classes.dropZone;
  };

  return (
    <div className={`${classes.container} ${props.className}`}>
      <div
        className={`${getDropZoneClassName()} ${
          props.error ? classes.dropZoneError : ""
        }`}
        onDragOver={dragHandler()}
        onDragEnter={dragHandler(e => {
          setRefCount(refCount => refCount + 1);
        })}
        onDrop={dragHandler(e => {
          setRefCount(0);
          fileChanged(e.dataTransfer.files[0]);
        })}
        onDragLeave={dragHandler(() => {
          setRefCount(refCount => refCount - 1);
        })}
      >
        <div className={classes.selectFileRow}>
          <input
            className={classes.fileInput}
            ref={fileInput => setFileInput(fileInput)}
            type="file"
            accept={props.accept}
            onChange={e => fileChanged(e.currentTarget.files[0])}
          />
          <Button
            classes={{ root: classes.button, label: classes.buttonLabel }}
            onClick={() => fileInput.click()}
            variant="text"
            color="primary"
            disabled={!!props.value || props.disabled}
          >
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <CloudUploadIcon
                className={classes.uploadIcon}
                color="primary"
                fontSize="large"
              />
              Choose a file
            </Stack>
          </Button>
        </div>
        {props.value && (
          <div className={classes.fileNameRow}>
            <span className={classes.fileName}>{props.value.name}</span>
            <ClearIcon onClick={() => fileRemoved()} />
          </div>
        )}
      </div>
      {props.error && (
        <div className={classes.errorRow}>
          <span className={classes.errorLabel}>{props.error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUploadInput;
